import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "flex items-center gap-2" };
const _hoisted_2 = { class: "flex flex-col" };
const _hoisted_3 = {
    id: "advanced-filter-library-title",
    class: "text-xl font-bold text-(--colors-text-heading)"
};
const _hoisted_4 = { class: "text-base text-(--colors-text-body-subtle)" };
const _hoisted_5 = { class: "flex min-h-[420px] flex-col gap-3" };
const _hoisted_6 = { class: "flex flex-col gap-3" };
const _hoisted_7 = {
    key: 1,
    class: "text-sm text-(--colors-text-body-subtle)"
};
const _hoisted_8 = ["role", "aria-labelledby", "tabindex"];
const _hoisted_9 = { class: "flex items-center gap-4" };
import { computed, toRef, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Modal, NavTabs, SearchInput, Button, TextLink } from "@eudonet/opal";
import { useAdvancedFilterLibrary } from "@/shared/composables/useAdvancedFilterLibrary";
import AdvancedFilterLibraryList from "./AdvancedFilterLibraryList.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'AdvancedFilterLibraryModal',
    props: {
        modelValue: { type: Boolean },
        entity: {},
        application: {},
        builderFields: {}
    },
    emits: ["update:modelValue", "apply", "create", "edit", "rename", "delete", "manage-access"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const lib = useAdvancedFilterLibrary({
            entity: props.entity,
            application: props.application,
            builderFields: toRef(props, "builderFields"),
        });
        // Load once each time the modal opens; reset state each time it closes.
        watch(() => props.modelValue, (open) => {
            if (open)
                void lib.load();
            else
                lib.reset();
        }, { immediate: true });
        // Single content region: only its active tab changes, so every tab's aria-controls points at the
        // same panel id, and the panel's aria-labelledby follows whichever tab is currently selected.
        const tabsGroupId = useId();
        const libraryPanelId = `${tabsGroupId}-panel`;
        function tabDomId(key) {
            return `${tabsGroupId}-tab-${key}`;
        }
        const activeTabDomId = computed(() => tabDomId(lib.activeTab.value));
        // APG: a tabpanel with no focusable descendant must itself take a tab stop. Items carry their
        // own tabindex, and among the empty states only `personal-empty` holds a focusable element (its
        // create CTA); while searching the region is not a tabpanel, so it never needs one there.
        const panelNeedsTabStop = computed(() => !lib.isSearching.value && lib.emptyState.value !== null && lib.emptyState.value !== "personal-empty");
        const tabs = computed(() => [
            {
                key: "personal",
                label: `${t("advancedFilterLibrary.tabs.personal")} (${lib.counts.value.personal})`,
                id: tabDomId("personal"),
                panelId: libraryPanelId,
            },
            {
                key: "shared",
                label: `${t("advancedFilterLibrary.tabs.shared")} (${lib.counts.value.shared})`,
                id: tabDomId("shared"),
                panelId: libraryPanelId,
            },
            {
                key: "eudonet",
                label: `${t("advancedFilterLibrary.tabs.eudonet")} (${lib.counts.value.eudonet})`,
                id: tabDomId("eudonet"),
                panelId: libraryPanelId,
            },
        ]);
        const activeTabModel = computed({
            get: () => lib.activeTab.value,
            set: (v) => (lib.activeTab.value = v),
        });
        function close() {
            emit("update:modelValue", false);
        }
        function onApply() {
            if (!lib.selectedId.value)
                return;
            emit("apply", lib.selectedId.value);
            close();
        }
        function onCreate() {
            emit("create");
            close();
        }
        // Close the library first so the follow-up modal (builder / rename / confirm)
        // is not stacked behind it.
        function onEdit(id) {
            emit("edit", id);
            close();
        }
        function onRename(id, name) {
            emit("rename", id, name);
            close();
        }
        function onDelete(id, name) {
            emit("delete", id, name);
            close();
        }
        function onManageAccess(id, name) {
            emit("manage-access", id, name);
            close();
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                "model-value": props.modelValue,
                size: "lg",
                "footer-position": "split",
                "aria-labelledby": "advanced-filter-library-title",
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => (emit('update:modelValue', $event)))
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _cache[5] || (_cache[5] = _createElementVNode("span", { class: "flex h-12 w-12 items-center justify-center rounded-lg border border-(--border-border-base-medium)" }, [
                            _createElementVNode("i", {
                                class: "ri-shuffle-line text-[28px]",
                                "aria-hidden": "true"
                            })
                        ], -1 /* CACHED */)),
                        _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("h4", _hoisted_3, _toDisplayString(_ctx.$t("advancedFilterLibrary.title")), 1 /* TEXT */),
                            _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t("advancedFilterLibrary.helper")), 1 /* TEXT */)
                        ])
                    ])
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(TextLink), {
                        "data-testid": "library-create",
                        "icon-left": "ri-add-line",
                        onClick: onCreate
                    }, {
                        default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$t("advancedFilterLibrary.footer.create")), 1 /* TEXT */)
                        ]),
                        _: 1 /* STABLE */
                    }),
                    _createElementVNode("div", _hoisted_9, [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _ctx.$t('advancedFilterLibrary.footer.cancel'),
                            onClick: close
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            "data-testid": "library-apply",
                            variant: "primary",
                            "icon-left": "ri-check-line",
                            label: _ctx.$t('advancedFilterLibrary.footer.apply'),
                            disabled: !_unref(lib).selectedId.value,
                            onClick: onApply
                        }, null, 8 /* PROPS */, ["label", "disabled"])
                    ])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                        _createElementVNode("div", _hoisted_6, [
                            (!_unref(lib).isSearching.value)
                                ? (_openBlock(), _createBlock(_unref(NavTabs), {
                                    key: 0,
                                    modelValue: activeTabModel.value,
                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((activeTabModel).value = $event)),
                                    tabs: tabs.value,
                                    "overflow-label": _ctx.$t('common.tabs.next'),
                                    "overflow-previous-label": _ctx.$t('common.tabs.previous')
                                }, null, 8 /* PROPS */, ["modelValue", "tabs", "overflow-label", "overflow-previous-label"]))
                                : (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_ctx.$t("advancedFilterLibrary.search.resultCount", { count: _unref(lib).resultCount.value })), 1 /* TEXT */)),
                            _createVNode(_unref(SearchInput), {
                                "model-value": _unref(lib).search.value,
                                rounded: "full",
                                width: '259px',
                                placeholder: _ctx.$t('advancedFilterLibrary.search.placeholder'),
                                labels: { clear: _ctx.$t('common.clearSearch') },
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (_unref(lib).search.value = $event))
                            }, null, 8 /* PROPS */, ["model-value", "placeholder", "labels"])
                        ]),
                        _createElementVNode("div", {
                            id: libraryPanelId,
                            role: !_unref(lib).isSearching.value ? 'tabpanel' : undefined,
                            "aria-labelledby": !_unref(lib).isSearching.value ? activeTabDomId.value : undefined,
                            tabindex: panelNeedsTabStop.value ? 0 : undefined,
                            class: "flex-1 overflow-y-auto max-h-[min(560px,60vh)]"
                        }, [
                            _createVNode(AdvancedFilterLibraryList, {
                                items: _unref(lib).visibleItems.value,
                                "selected-id": _unref(lib).selectedId.value,
                                "empty-state": _unref(lib).emptyState.value,
                                "tooltip-text-by-id": _unref(lib).tooltipTextById.value,
                                onSelect: _cache[2] || (_cache[2] = ($event) => (_unref(lib).select($event))),
                                onRequestTooltip: _cache[3] || (_cache[3] = ($event) => (_unref(lib).loadTooltip($event))),
                                onCreate: onCreate,
                                onEdit: onEdit,
                                onRename: onRename,
                                onDelete: onDelete,
                                onManageAccess: onManageAccess
                            }, null, 8 /* PROPS */, ["items", "selected-id", "empty-state", "tooltip-text-by-id"])
                        ], 8 /* PROPS */, _hoisted_8)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["model-value"]));
        };
    }
});
